<template>
  <div class="form-group">
    <div class="shipping-address-heading">
      <div class="label required fs-xl">Shipping address</div>
      <div class="d-flex gap-2">
        <b-button v-if="isAddressBookAvailable" class="ship-to-terminal-btn" @click="openAddressBookPicker">Address Book</b-button>
        <b-button class="ship-to-terminal-btn" @click="fillTerminalAddress">Ship to Terminal</b-button>
      </div>
    </div>
    <AddressBookPickerModal />

    <div class="row mt-4 shipping-address-form">
      <div class="col-md-6">
        <div class="form-group">
          <label>Company</label>
          <span class="company-name-info">
            <img src="@/assets/img/info-circle-outlined.svg" alt="" class="mr-1" />
            Insert only, if sending to company
          </span>
          <VInput
            v-model="recipientAddress.company_name"
            :rules="{ required: false, min: 3 }"
            type="text"
            vid="item.company_name"
            name="item.company_name"
            placeholder="Company"
          />
        </div>
        <div class="d-flex flex-row">
          <div class="form-group pl-0 m-0 col-6 pr-12">
            <label class="required">First Name</label>
            <VInput
              v-model="recipientAddress.first_name"
              type="text"
              :rules="{ required: true }"
              vid="item.first_name"
              name="item.first_name"
              placeholder="First Name *"
            />
          </div>
          <div class="form-group p-0 m-0 col-6">
            <label>Last Name</label>
            <VInput
              v-model="recipientAddress.last_name"
              type="text"
              :rules="{ permissible: true }"
              vid="item.last_name"
              name="item.last_name"
              placeholder="Last Name"
            />
          </div>
        </div>
        <div class="form-group mb-0">
          <label class="required">Address 1</label>
          <VInput
            v-model="recipientAddress.address"
            :rules="{ required: true, min: 3, max: 30 }"
            type="text"
            vid="item.address"
            name="item.address"
            placeholder="Client Address 1 *"
          />
        </div>
        <div class="form-group">
          <label>Address 2</label>
          <VInput
            v-model="recipientAddress.secondary_address"
            :rules="{ required: false, min: 3, max: 30 }"
            type="text"
            vid="item.secondary_address"
            name="item.secondary_address"
            placeholder="Client Address 2"
          />
        </div>
        <div class="d-flex flex-row">
          <div class="form-group mb-0">
            <label class="required">ZIP</label>
            <VInput
              v-model="recipientAddress.postal_code"
              type="text"
              :rules="{ required: true, zip: recipientAddress.country_code }"
              vid="postal_code"
              name="item.postal_code"
              placeholder="ZIP *"
            />
          </div>
          <div class="form-group client-city mb-0">
            <label :class="{ required: true }">City</label>
            <VInputDatalist
              id="city"
              v-model="recipientAddress.city"
              type="text"
              :list="cities"
              :rules="{ required: true }"
              vid="item.city"
              name="item.city"
              placeholder="City *"
              @input="onCityUpdate"
            />
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="form-group">
          <div class="form-group">
            <label class="required">Country</label>
            <CountryPicker
              :value="recipientAddress.country_code"
              :disabled="false"
              vinput-id="country"
              name="item.country_code"
              vid="country_code"
              placeholder="Choose country *"
              :required="true"
              @change="onCountryUpdate"
            />
          </div>
          <div class="form-group">
            <label
              :class="{
                required: (isSwedenPicked && !recipientAddress.phone_number?.length) || !isSwedenPicked,
              }"
            >
              Email Address
            </label>
            <VInput
              v-model="recipientAddress.email"
              type="text"
              vid="item.email"
              name="item.email"
              :rules="rules.emailRule"
              :placeholder="
                (isSwedenPicked && !recipientAddress.phone_number?.length) || !isSwedenPicked
                  ? 'Email address *'
                  : 'Email address'
              "
            />
          </div>
          <div class="form-group">
            <label
              :class="{
                required: (isSwedenPicked && !recipientAddress.email?.length) || !isSwedenPicked,
              }"
            >
              SMS-Number
            </label>
            <VInput
              v-model="recipientAddress.phone_number"
              type="text"
              vid="item.phone_number"
              name="item.phone_number"
              :rules="rules.phoneRule"
              :formatter="numbersOnly"
              :placeholder="
                (isSwedenPicked && !recipientAddress.email?.length) || !isSwedenPicked ? 'SMS-Number *' : 'SMS-Number'
              "
            />
          </div>
        </div>
      </div>
    </div>
    <template v-if="isAddressBookAvailable">
      <div v-if="filledFromAddressBook" class="form-check mt-2">
        <input id="updateInAddressBook" v-model="saveToAddressBook" type="checkbox" class="form-check-input" />
        <label class="form-check-label" for="updateInAddressBook">Update in address book after assignment is created</label>
      </div>
      <div v-else class="form-check mt-2">
        <input id="saveToAddressBook" v-model="saveToAddressBook" type="checkbox" class="form-check-input" />
        <label class="form-check-label" for="saveToAddressBook">Save to address book after assignment is created</label>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import type { AddressBook } from '@/types/Models/AddressBook'
import type { AssignmentCreationData, AssignmentCreationItemData } from '@/types/Models/Assignment'
import { BButton } from 'bootstrap-vue-next'
import { ref, defineComponent, type Ref, type ComputedRef, computed, watch, onMounted, onBeforeMount } from 'vue'
import { useCities } from '@/composables/AssignmentForm/useCities'
import { useValidationRules } from '@/composables/AssignmentForm/useValidationRules'
import { ADDRESS_BOOK_PICKER } from '@/const/ModalTypes'
import { numbersOnly } from '@/services/Helpers'
import { openModal, registerModals } from '@/services/Modal'
import { useAccountStore } from '@/store/account'
import { useAssignmentFormStore } from '@/store/assignmentForm'
import { useCustomersStore } from '@/store/customers'
import VInput from '@/views/Components/Elements/Form/VInput.vue'
import CountryPicker from '@/views/Components/Elements/Form/CountryPicker.vue'
import VInputDatalist from '@/views/Components/Elements/Form/VInputDatalist.vue'
import terminalAddressInfo from '@/assets/files/terminalAddressInfo.json'
import AddressBookPickerModal from '@/views/Modals/AddressBook/AddressBookPickerModal.vue'

export default defineComponent({
  name: 'RecipientAddress',
  components: { BButton, CountryPicker, VInput, VInputDatalist, AddressBookPickerModal },
  setup() {
    const accountStore = useAccountStore()
    const customersStore = useCustomersStore()
    const assignmentFormStore = useAssignmentFormStore()
    const cities = useCities()

    const filledFromAddressBook: Ref<boolean> = ref(false)
    const saveToAddressBook = computed({
      get: () => assignmentFormStore.saveToAddressBook,
      set: (value: boolean) => assignmentFormStore.setSaveToAddressBook(value),
    })

    const recipientAddress: Ref<Partial<AssignmentCreationItemData>> = ref({
      company_name: '',
      first_name: '',
      last_name: '',
      address: '',
      secondary_address: '',
      postal_code: '',
      city: '',
      country_code: '',
      email: '',
      phone_number: '',
    })
    const assignment: ComputedRef<AssignmentCreationData> = computed(() => assignmentFormStore.assignment)
    const isSwedenPicked = computed(() => assignment.value.item.country_code === 'SE')
    const companyId = computed(() => assignment.value.company_id)
    const isAddressBookAvailable = computed(() => {
      if (!accountStore.isStaff) {
        return customersStore.customer?.isAddressBookAvailable ?? false
      }
      return customersStore.allCustomers.find((c) => c.id === companyId.value)?.isAddressBookAvailable ?? false
    })

    const rules = useValidationRules(assignment)

    const onCityUpdate = (value: string) => {
      recipientAddress.value.city = value
    }

    const onCountryUpdate = (value: string) => {
      recipientAddress.value.country_code = value
    }

    const fillFromAddressBook = (entry: AddressBook) => {
      filledFromAddressBook.value = true
      assignmentFormStore.setAddressBookEntry(entry.id)
      assignmentFormStore.setSaveToAddressBook(false)
      recipientAddress.value.first_name = entry.firstName ?? ''
      recipientAddress.value.last_name = entry.lastName ?? ''
      recipientAddress.value.company_name = entry.company ?? ''
      recipientAddress.value.country_code = entry.countryCode
      recipientAddress.value.postal_code = entry.postalCode ?? ''
      recipientAddress.value.city = entry.city ?? ''
      recipientAddress.value.address = entry.addressLine ?? ''
      recipientAddress.value.secondary_address = entry.addressLine2 ?? ''
      recipientAddress.value.phone_number = entry.phoneNumber ?? ''
      recipientAddress.value.email = entry.email ?? ''
    }

    const openAddressBookPicker = () => {
      openModal(ADDRESS_BOOK_PICKER, { companyId: companyId.value, onSelect: fillFromAddressBook })
    }

    const fillTerminalAddress = () => {
      filledFromAddressBook.value = false
      assignmentFormStore.setAddressBookEntry(null)
      assignmentFormStore.setSaveToAddressBook(false)
      recipientAddress.value.city = terminalAddressInfo.city
      recipientAddress.value.email = terminalAddressInfo.email
      recipientAddress.value.first_name = terminalAddressInfo.first_name
      recipientAddress.value.company_name = terminalAddressInfo.company_name
      recipientAddress.value.phone_number = terminalAddressInfo.phone_number
      recipientAddress.value.country_code = terminalAddressInfo.country_code
      recipientAddress.value.postal_code = terminalAddressInfo.postal_code
      recipientAddress.value.address = terminalAddressInfo.address
    }

    onBeforeMount(() => {
      registerModals(ADDRESS_BOOK_PICKER)
    })

    onMounted(() => {
      recipientAddress.value = { ...recipientAddress.value, ...assignment.value.item }
    })

    watch(companyId, () => {
      filledFromAddressBook.value = false
    })

    watch(
      recipientAddress,
      (newValue) => {
        assignmentFormStore.setFields({ item: { ...assignment.value.item, ...newValue } })
      },
      { immediate: true, deep: true },
    )

    return {
      rules,
      cities,
      assignment,
      isSwedenPicked,
      isAddressBookAvailable,
      filledFromAddressBook,
      saveToAddressBook,
      recipientAddress,
      numbersOnly,
      onCityUpdate,
      onCountryUpdate,
      fillTerminalAddress,
      openAddressBookPicker,
    }
  },
})
</script>
